import React, {useEffect, useRef, useCallback} from 'react'
import Hammer from 'hammerjs'
import {Line} from 'react-chartjs-2'
import {Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend} from 'chart.js'
import PropTypes from 'prop-types'
import zoomPlugin from 'chartjs-plugin-zoom'
import {calculateYlabels, customTooltipCallback, generateMinMaxOnZoom, splitString} from './helper'
import annotationPlugin from 'chartjs-plugin-annotation'
import {handleResetZoom, handleZoomOnWheel, handleZoomIn, handleZoomOut, handleZoomOnPinch} from './zoomHelper'
import {useTheme} from '@mui/material/styles'
import {customBarsPlugin} from './plugins/customBar'
import {htmlLegendPlugin} from './plugins/htmlLegend'
import {markerOnEnter, markerOnLeave, markerPlugin, updateLabelsAlignments, generateMarkerData} from './plugins/marker'
import {useChartMarker} from '../../store/hooks/chartMarker'
import {Box} from '@mui/material'
import {useChangeLabelColorOnUnmount} from '../hooks'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  customBarsPlugin,
  annotationPlugin,
  markerPlugin
)

const LineChart = ({
  dataSets = [],
  yMin = 0,
  yMax = 100000,
  xLabels = [],
  displayLegend = false,
  tooltipPosition = 'bottom',
  setZoomProps,
  chartRef,
  name,
  setTooltipVisible,
  setShowMarkerTooltip,
  setClickAwayEnabled
}) => {
  const theme = useTheme()
  const pinchState = useRef(null)
  const [yMinRound, yMaxRound, stepCount] = calculateYlabels(yMin, yMax)
  const data = {
    labels: xLabels,
    datasets: dataSets
  }

  const zoomLimits = {
    y: {max: displayLegend ? yMaxRound : 100000, min: displayLegend ? yMinRound : 0, minRange: 2},
    x: {min: xLabels.at(0), max: xLabels.at(-1)}
  }

  let mc
  const {chartMarkers} = useChartMarker()
  useEffect(() => {
    if (displayLegend && chartRef?.current?.canvas) {
      setZoomProps((zoomProperties) => ({
        ...zoomProperties,
        reset: true,
        in: false,
        out: true,

        handleZoomOut: () => handleZoomOut(chartRef, zoomLimits, setZoomProps),
        handleZoomIn: () => handleZoomIn(chartRef, zoomLimits, setZoomProps),
        handleResetZoom: () => handleResetZoom(chartRef, stepCount)
      }))

      // eslint-disable-next-line react-hooks/exhaustive-deps
      mc = new Hammer.Manager(chartRef.current.canvas)
      mc.add(new Hammer.Pinch())
      mc.on('pinchstart', () => {
        if (displayLegend) {
          pinchState.current = 1
        }
      })
      mc.on('pinch', (e) => handleZoomOnPinch(e, zoomLimits, pinchState, chartRef, setZoomProps))
      mc.on('pinchend', () => {
        if (pinchState.current) {
          pinchState.current = null
        }
      })

      chartRef.current.canvas.addEventListener('wheel', (e) => handleZoomOnWheel(e, zoomLimits, chartRef, setZoomProps))

      // Initialize marker with lineLabels from Redux
      const savedLineLabels = chartMarkers.current?.[name]?.lineLabels || {}

      // Recreate annotations from saved lineLabels
      const recreatedAnnotations = {}
      for (const key of Object.keys(savedLineLabels)) {
        const label = savedLineLabels[key]
        if (label) {
          const str = splitString(label)
          if (str) {
            recreatedAnnotations[key] = generateMarkerData(Number.parseInt(key), str)
          }
        }
      }

      // Preserve existing marker properties from plugin initialization and add new ones
      chartRef.current.marker = {
        ...chartRef.current.marker,
        lineLabels: savedLineLabels,
        annotations: recreatedAnnotations,
        name,
        x: chartRef.current.marker?.x || 0,
        y: chartRef.current.marker?.y || 0,
        draw: false
      }
      chartRef.current.setTooltipVisible = setTooltipVisible
      chartRef.current.setClickAwayEnabled = setClickAwayEnabled
      updateLabelsAlignments(chartMarkers.current?.[name]?.lineLabels || {}, 0, chartRef.current, null, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayLegend])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const customTooltip = useCallback((context) => {
    customTooltipCallback(context, chartRef, tooltipPosition, setTooltipVisible)
  })

  // Helper function to get or recreate annotations from Redux lineLabels
  const getAnnotations = useCallback(() => {
    // If chart.marker.annotations exists, use it
    if (chartRef.current?.marker?.annotations) {
      return chartRef.current.marker.annotations
    }

    // Otherwise, recreate from Redux lineLabels
    const savedLineLabels = chartMarkers.current?.[name]?.lineLabels || {}
    const recreatedAnnotations = {}
    for (const key of Object.keys(savedLineLabels)) {
      const label = savedLineLabels[key]
      if (label) {
        const str = splitString(label)
        if (str) {
          recreatedAnnotations[key] = generateMarkerData(Number.parseInt(key), str)
        }
      }
    }
    return recreatedAnnotations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMarkers, name])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        major: {enabled: true},
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          textStrokeWidth: 200
        },
        grid: {
          display: true,
          drawOnChartArea: false,
          drawTicks: true,
          tickColor: (c) => {
            if (c.tick.label) return theme.palette.grey[600]
            else return theme.palette.hpBaseLight
          }
        },
        display: true,
        title: {
          display: true,
          text: 'Year',
          color: theme.palette.hpAccentBlack,
          font: {
            weight: theme.typography.fontWeightBold
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Adult Population Per 100k',
          color: theme.palette.hpAccentBlack,
          font: {
            weight: theme.typography.fontWeightBold
          }
        },
        min: displayLegend ? yMinRound : 0,
        max: displayLegend ? yMaxRound : 100000,
        ticks: {
          stepSize: displayLegend ? stepCount : 10000,
          callback: function (value, index, values) {
            const ticksBelow1000 = [100, 200, 300, 400, 500, 600, 700, 800, 900]
            const mod = value % 1000
            if (mod !== 0) {
              if (ticksBelow1000.includes(value % 1000)) {
                return value
              } else if (index === 0 || index === values.length - 1) {
                return Number.parseInt(value / 1000) + 'k'
              } else return ''
            }

            const yValue = Number.parseInt(value / 1000)
            if (yValue === 0) return yValue + ''
            return yValue + 'k'
          }
        }
      }
    },
    plugins: {
      annotation: {
        common: {
          drawTime: 'afterDatasetsDraw'
        },
        clip: false,
        animations: false,
        enter: markerOnEnter,
        leave: markerOnLeave,
        annotations: getAnnotations()
      },
      drawBars: {
        spacing: 10, // Adjust this value to control the spacing between bars
        backgroundColor: 'rgba(0, 0, 0, 0.1)' // Grey color for the bars
      },
      legend: {
        display: false
      },
      htmlLegend: {
        containerID: `legend-container-${name}`
      },
      tooltip: {
        enabled: false,
        animation: false,
        position: 'nearest',
        external: customTooltip
      },
      zoom: {
        zoom: {
          drag: {
            enabled: displayLegend,
            modifierKey: 'ctrl',
            backgroundColor: 'rgba(21,645,219,0.3)'
          },
          mode: 'y',
          onZoomComplete: function ({chart}) {
            if (chart.getZoomLevel() > 1) {
              setZoomProps((prev) => ({...prev, reset: false, out: false}))
            } else {
              setZoomProps((prev) => ({...prev, reset: true, out: true, in: false}))
            }
          }
        },
        pan: {
          enabled: true,
          mode: 'y',
          onPan: function ({chart}) {
            if (chart.getZoomLevel() > 1) chart.ctx.canvas.style.cursor = 'grabbing'
          },
          onPanComplete: function ({chart}) {
            if (chart.getZoomLevel() > 1) chart.ctx.canvas.style.cursor = 'grab'
            const res = generateMinMaxOnZoom(chart.scales.y.min, chart.scales.y.max, zoomLimits.y.min, zoomLimits.y.max)
            chart.scales.y.options.ticks.stepSize = res.diff
            chart.zoomScale('y', res, 'none')
            chart.scales.y.options.min = res.min
            chart.scales.y.options.max = res.max
            if (chart.getZoomLevel() > 1)
              setZoomProps((zoomProps) => ({...zoomProps, out: false, reset: false, in: res.max - res.min <= 1000}))
          }
        },
        limits: zoomLimits
      },

      marker: {
        color: theme.palette.hpAccentBlack,
        enabled: chartRef.current?.options?.plugins?.marker?.enabled || false,
        editEnabled: chartRef.current?.options?.plugins?.marker?.editEnabled || false
      }
    },
    onClick: function (e, item) {
      if (!e.chart.options.plugins.marker.enabled && !e.chart.options.plugins.marker.editEnabled) {
        if (item.length > 0) {
          e.chart.options.plugins.tooltip.events = ['click']
          e.chart?.setClickAwayEnabled(true)
        } else {
          e.chart.tooltip._active = []
          e.chart.tooltip.update(true)
          e.chart.options.plugins.tooltip.events = ['mousemove', 'mouseout', 'touchstart', 'touchmove', 'touchend']
        }
        e.chart.update()
      }
    },
    onHover: function (e, item) {
      if (e.chart?.labelHovered) return
      if (item.length > 0) {
        e.chart.ctx.canvas.style.cursor = 'pointer'
      } else if (e.chart.getZoomLevel() > 1) {
        e.chart.ctx.canvas.style.cursor = 'grab'
      } else e.chart.ctx.canvas.style.cursor = 'default'
    },
    setShowMarkerTooltip
  }
  useChangeLabelColorOnUnmount(chartRef)

  return (
    <Box sx={{width: '95%'}}>
      <Line
        style={{height: '300px'}}
        updateMode='none'
        ref={chartRef}
        data={data}
        options={options}
        plugins={[htmlLegendPlugin, markerPlugin]}
        data-testid='line-chart'
      />
    </Box>
  )
}
LineChart.displayName = 'LineChart'
const MemoizedLineChart = React.memo(LineChart, (prevProps, nextProps) => prevProps.xLabels === nextProps.xLabels)

LineChart.propTypes = {
  dataSets: PropTypes.array,
  yMin: PropTypes.number,
  yMax: PropTypes.number,
  xLabels: PropTypes.array,
  displayLegend: PropTypes.bool,
  tooltipPosition: PropTypes.string,
  setZoomProps: PropTypes.func,
  chartRef: PropTypes.object,
  name: PropTypes.string,
  setTooltipVisible: PropTypes.func,
  setShowMarkerTooltip: PropTypes.func,
  setClickAwayEnabled: PropTypes.func
}

export default MemoizedLineChart

/**
 * LineChart Component:
 * - Renders a responsive line chart using Chart.js with various plugins and customizations.
 * - Handles zooming, panning, and pinch gestures using Hammer.js and chartjs-plugin-zoom.
 * - Customizes tooltips, annotations, and marker interactions for enhanced user experience.
 * - Dynamically updates chart labels, scales, and zoom properties based on user interactions.
 * - Integrates with external marker and legend plugins for additional chart features.
 * - Provides callbacks for hover, click, and zoom events to manage chart state and interactivity.
 */
